import React, { createContext, useContext, useId } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

const FormFieldCtx = createContext(null);
export const useFormField = () => useContext(FormFieldCtx);

/* -------------------------------------------------------------------------- */
/*  FormField — wrapper qui donne id, aria-* à un input enfant                */
/* -------------------------------------------------------------------------- */

export function FormField({
  label,
  helper,
  error,
  required = false,
  optional = false,
  hint,
  htmlFor,
  className,
  children,
  ...rest
}) {
  const autoId = useId();
  const id = htmlFor || autoId;
  const helperId = helper ? `${id}-helper` : undefined;
  const errorId  = error ? `${id}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <FormFieldCtx.Provider value={{ id, describedBy, invalid: Boolean(error), required }}>
      <div className={cn('flex flex-col gap-1.5', className)} {...rest}>
        {label && <Label htmlFor={id} required={required} optional={optional} hint={hint}>{label}</Label>}
        {children}
        {error
          ? <ErrorText id={errorId}>{error}</ErrorText>
          : helper && <HelperText id={helperId}>{helper}</HelperText>}
      </div>
    </FormFieldCtx.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/*  Label                                                                     */
/* -------------------------------------------------------------------------- */

export function Label({ htmlFor, required, optional, hint, className, children, ...rest }) {
  return (
    <label htmlFor={htmlFor} className={cn('flex items-baseline gap-2 text-sm font-semibold text-text', className)} {...rest}>
      <span>
        {children}
        {required && <span className="text-danger ml-0.5" aria-label="requis">*</span>}
      </span>
      {optional && !required && <span className="text-xs text-mute font-normal">(facultatif)</span>}
      {hint && <span className="ml-auto text-xs text-mute font-normal">{hint}</span>}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*  HelperText / ErrorText                                                    */
/* -------------------------------------------------------------------------- */

export function HelperText({ id, className, children, ...rest }) {
  return <p id={id} className={cn('text-xs text-mute leading-snug', className)} {...rest}>{children}</p>;
}

export function ErrorText({ id, className, children, ...rest }) {
  return (
    <p id={id} role="alert" className={cn('inline-flex items-start gap-1 text-xs text-danger leading-snug font-medium', className)} {...rest}>
      <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2} />
      <span>{children}</span>
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/*  Fieldset                                                                  */
/* -------------------------------------------------------------------------- */

export function Fieldset({ legend, description, className, children, ...rest }) {
  return (
    <fieldset className={cn('border border-border rounded-lg p-4 bg-surface', className)} {...rest}>
      {legend && <legend className="text-sm font-semibold text-text px-1.5">{legend}</legend>}
      {description && <p className="text-xs text-mute mb-3">{description}</p>}
      <div className="space-y-3">{children}</div>
    </fieldset>
  );
}

export default FormField;
